import React, { useState, useEffect } from 'react';
import { Card, Button, Badge, Row, Col, Spinner, Alert, Form, Modal, Image, Container } from 'react-bootstrap';
import { FaPlus, FaHeart, FaComment, FaEye, FaFlag, FaReply, FaArrowLeft, FaThumbsUp } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { decodeForumId } from '../../../utils/urlEncryption';

const ForumDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [forum, setForum] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [commentForm, setCommentForm] = useState({
    content: '',
    attachment: null
  });
  const [submitting, setSubmitting] = useState(false);
  const [likedForums, setLikedForums] = useState(new Set());
  const [likedComments, setLikedComments] = useState(new Set());

  useEffect(() => {
    fetchForum();
    fetchComments();
  }, [id]);

  useEffect(() => {
    console.log('Liked comments state:', Array.from(likedComments));
  }, [likedComments]);

  const fetchForum = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let decodedId = decodeForumId(id);
      if (!decodedId) {
        const numericId = parseInt(id);
        if (!isNaN(numericId)) {
          decodedId = numericId;
        } else {
          setError('Invalid forum ID');
          return;
        }
      }
      
      const response = await api.forums.getById(decodedId);
      
      if (response.data.success) {
        setForum(response.data.data);
      } else {
        setError(response.data.message || 'Failed to fetch forum');
      }
    } catch (error) {
      console.error('Error fetching forum:', error);
      setError('Failed to fetch forum. Please try again.');
      toast.error('Failed to fetch forum');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async () => {
    try {
      setCommentsLoading(true);
      
      const decodedId = decodeForumId(id);
      if (!decodedId) {
        toast.error('Invalid forum ID');
        return;
      }
      
      const response = await api.forums.getComments(decodedId, {
        page: currentPage,
        limit: 20
      });
      
      if (response.data.success) {
        setComments(response.data.data.comments);
        setTotalPages(response.data.data.totalPages);
      } else {
        toast.error(response.data.message || 'Failed to fetch comments');
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
      toast.error('Failed to fetch comments');
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleLikeForum = async () => {
    try {
      const decodedId = decodeForumId(id);
      if (!decodedId) return;
      
      const response = await api.forums.toggleLike(decodedId);
      
      if (response.data.success) {
        setForum(prev => ({
          ...prev,
          likes_count: response.data.likesCount
        }));
        
        if (response.data.liked) {
          setLikedForums(prev => new Set([...prev, id]));
        } else {
          setLikedForums(prev => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
        }
      } else {
        toast.error(response.data.message || 'Failed to like forum');
      }
    } catch (error) {
      console.error('Error liking forum:', error);
      toast.error('Failed to like forum');
    }
  };

  const handleLikeComment = async (commentId) => {
    try {
      const response = await api.forums.toggleCommentLike(commentId);
      
      if (response.data.success) {
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, likes_count: response.data.likesCount }
            : comment
        ));
        
        if (response.data.liked) {
          setLikedComments(prev => new Set([...prev, commentId]));
        } else {
          setLikedComments(prev => {
            const newSet = new Set(prev);
            newSet.delete(commentId);
            return newSet;
          });
        }
      }
    } catch (error) {
      console.error('Error liking comment:', error);
      toast.error('Failed to like comment');
    }
  };

  const handleHighlightComment = async (commentId) => {
    if (!window.confirm('Are you sure you want to mark this comment as vulgar?')) {
      return;
    }

    try {
      const response = await api.forums.highlightComment(commentId, 'Marked as vulgar content');
      
      if (response.data.success) {
        toast.success('Comment highlighted for moderation');
        setComments(prev => prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, is_highlighted: 1 }
            : comment
        ));
      } else {
        toast.error(response.data.message || 'Failed to highlight comment');
      }
    } catch (error) {
      console.error('Error highlighting comment:', error);
      toast.error('Failed to highlight comment');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!commentForm.content.trim()) {
      toast.error('Comment content is required');
      return;
    }

    try {
      setSubmitting(true);
      const decodedId = decodeForumId(id);
      if (!decodedId) {
        toast.error('Invalid forum ID');
        return;
      }
      const response = await api.forums.createComment(decodedId, commentForm);
      
      if (response.data.success) {
        toast.success('Comment added successfully');
        setShowCommentModal(false);
        setCommentForm({ content: '', attachment: null });
        fetchComments();
      } else {
        toast.error(response.data.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCommentForm(prev => ({ ...prev, attachment: file }));
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const canHighlightComment = () => {
    return user.role === 1 || user.role === 2;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="danger" className="bg-red-50 border border-red-200 text-red-800 rounded-xl">
          {error}
        </Alert>
        <Button variant="outline-primary" onClick={() => navigate('/admin/forums')} className="mt-4 inline-flex items-center gap-2 border-purple-600 text-purple-600 hover:bg-purple-50">
          <FaArrowLeft className="w-4 h-4" />
          Back to Forums
        </Button>
      </div>
    );
  }

  if (!forum) {
    return (
      <div className="p-6">
        <Alert variant="warning" className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl">
          Forum not found
        </Alert>
        <Button variant="outline-primary" onClick={() => navigate('/admin/forums')} className="mt-4 inline-flex items-center gap-2 border-purple-600 text-purple-600 hover:bg-purple-50">
          <FaArrowLeft className="w-4 h-4" />
          Back to Forums
        </Button>
      </div>
    );
  }

  return (
    <Container fluid className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/forums')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-purple-300 text-purple-700 rounded-lg shadow-md hover:bg-purple-50 hover:shadow-lg transition-all duration-200 text-sm font-medium"
          >
            <FaArrowLeft className="w-4 h-4" /> Back
          </button>
          <h5 className="m-0 text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          Forum Discussion
          </h5>
        </div>
      </div>

      <Row className="py-2">
        <Col lg={8}>
          <Card className="mb-6 rounded-2xl shadow-xl overflow-hidden border-0">
            <Card.Header className="bg-white border-b border-purple-100 px-8 py-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="mb-3 text-3xl font-extrabold text-gray-800 leading-tight">
                    {forum.title}
                  </h3>
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <span className="text-gray-600">
                      by <strong className="text-purple-700">{forum.creator?.first_name} {forum.creator?.last_name}</strong>
                    </span>
                    <Badge bg={forum.org_id ? 'info' : 'success'} className="px-3 py-1 text-xs font-bold">
                      {forum.org_id ? 'Organization' : 'Global'}
                    </Badge>
                    <span className="text-gray-500">{formatDate(forum.createdAt)}</span>
                  </div>
                </div>
              </div>
            </Card.Header>
            
            {forum.attachment && (
              <Card.Img
                variant="top"
                src={getAssetUrl(forum.attachment)}
                className="w-full h-96 object-cover"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <Card.Body className="px-8 py-6">
              <Card.Text className="text-lg text-gray-700 leading-relaxed whitespace-pre-wrap">
                {forum.description}
              </Card.Text>
            </Card.Body>
            <Card.Footer className="bg-gradient-to-r from-purple-50 to-pink-50 px-8 py-5 flex flex-wrap items-center gap-6 text-lg">
              <a
                href="#"
                rel="noopener noreferrer"
                onClick={(e) => { e.preventDefault(); handleLikeForum(); }}
                className="inline-flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold transition"
              >
                <FaHeart className="w-6 h-6" />
                {forum.likes_count > 0 && <span>{forum.likes_count} Likes</span>}
              </a>

              <div className="flex items-center gap-2 text-gray-600">
                <FaComment className="w-5 h-5" />
                <small>{comments.length > 0 ? `${comments.length} Comments` : 'No Comments'}</small>
              </div>

              <div className="flex items-center gap-2 text-gray-600">
                <FaEye className="w-5 h-5" />
                <small>{forum.views_count || 0} Views</small>
              </div>
            </Card.Footer>
          </Card>

          <div className="flex justify-end mb-6">
            <Button
              bsPrefix="custom-hamburger"
              onClick={() => setShowCommentModal(true)}
              className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
            >
              <FaPlus className="w-5 h-5" />
              Add Comment
            </Button>
          </div>

          <Card className="rounded-2xl shadow-xl overflow-hidden">
            <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-2">
              <h5 className="m-0 text-xl font-bold">
                {comments.length > 0 ? `Comments (${comments.length})` : 'Comments'}
              </h5>
            </Card.Header>
            <Card.Body className="p-8">
              {commentsLoading ? (
                <div className="text-center py-12">
                  <Spinner animation="border" size="sm" className="text-purple-600" />
                  <span className="ms-3 text-gray-600">Loading comments...</span>
                </div>
              ) : comments.length === 0 ? (
                <div className="text-center py-16 text-gray-500">
                  <FaComment className="w-20 h-20 mx-auto mb-4 opacity-40" />
                  <p className="text-xl font-semibold">No comments yet. Be the first to comment!</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {comments.map(comment => (
                    <div key={comment.id} className="border-b border-purple-100 pb-8 last:border-0">
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-lg font-bold">
                            {(comment.user?.first_name?.[0] || '') + (comment.user?.last_name?.[0] || '')}
                          </div>
                          <div>
                            <strong className="block text-lg text-gray-800">
                              {comment.user?.first_name} {comment.user?.last_name}
                            </strong>
                            <small className="text-gray-500">
                              {formatDate(comment.createdAt)}
                            </small>
                          </div>
                        </div>
                        {comment.is_highlighted !== 0 && (
                          <Badge bg="warning" className="bg-red-100 text-red-800 flex items-center gap-1 px-3 py-1">
                            <FaFlag className="w-4 h-4" />
                            Flagged
                          </Badge>
                        )}
                      </div>
                      
                      <div className="ms-16">
                        <p className="mb-4 text-gray-700 leading-relaxed whitespace-pre-wrap">
                          {comment.content}
                        </p>
                        
                        {comment.attachment && (
                          <div className="mb-6">
                            <Image
                              src={getAssetUrl(comment.attachment)}
                              alt="Comment attachment"
                              className="max-w-sm rounded-xl shadow-lg"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          </div>
                        )}
                        
                        <div className="flex gap-4 items-center">
                          <Button
                          bsPrefix="custom-hamburger"
                            variant={likedComments.has(comment.id) ? 'primary' : 'outline-primary'}
                            onClick={(e) => { e.preventDefault(); handleLikeComment(comment.id); }}
                            className={`inline-flex items-center gap-2 ${likedComments.has(comment.id) ? 'text-purple-600' : 'border-purple-400 text-purple-600 hover:bg-purple-50'}`}
                          >
                            <FaThumbsUp className="w-4 h-4" />
                            {comment.likes_count > 0 && comment.likes_count}
                          </Button>
                          
                          {canHighlightComment() && !comment.is_highlighted && (
                            <Button
                              bsPrefix="custom-hamburger"
                              variant="outline-warning"
                              size="sm"
                              onClick={() => handleHighlightComment(comment.id)}
                              className="border-orange-400 text-orange-600 hover:bg-orange-50 inline-flex items-center gap-2"
                            >
                              <FaFlag className="w-4 h-4" />
                              Flag as Vulgar
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {totalPages > 1 && (
                <div className="flex justify-center mt-10">
                  <nav>
                    <ul className="inline-flex items-center gap-2">
                      <li className={`page-item ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <button
                          className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition"
                          onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                          disabled={currentPage === 1}
                        >
                          Previous
                        </button>
                      </li>
                      {[...Array(totalPages)].map((_, index) => (
                        <li key={index + 1} className={`page-item ${currentPage === index + 1 ? 'font-bold' : ''}`}>
                          <button
                            className={`w-10 h-10 rounded-lg transition ${currentPage === index + 1 ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-700 hover:bg-purple-200'}`}
                            onClick={() => setCurrentPage(index + 1)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        <button
                          className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition"
                          onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                          disabled={currentPage === totalPages}
                        >
                          Next
                        </button>
                      </li>
                    </ul>
                  </nav>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="rounded-2xl shadow-xl h-fit sticky-top">
            <Card.Header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-2">
              <h6 className="m-0 text-lg font-bold">Forum Information</h6>
            </Card.Header>
            <Card.Body className="p-6 space-y-6">
              <div>
                <strong className="block text-gray-700 mb-1">Created by:</strong>
                <p className="m-0 font-semibold text-purple-700">{forum.creator?.first_name} {forum.creator?.last_name}</p>
                <small className="text-gray-500">{forum.creator?.email}</small>
              </div>
              
              {forum.organization && (
                <div>
                  <strong className="block text-gray-700 mb-1">Organization:</strong>
                  <p className="m-0 font-semibold text-purple-700">{forum.organization.name}</p>
                </div>
              )}
              
              <div>
                <strong className="block text-gray-700 mb-1">Created:</strong>
                <p className="m-0">{formatDate(forum.createdAt)}</p>
              </div>
              
              <div>
                <strong className="block text-gray-700 mb-1">Last Updated:</strong>
                <p className="m-0">{formatDate(forum.updatedAt)}</p>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showCommentModal} onHide={() => setShowCommentModal(false)} fullscreen="sm-down" centered >
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:py-4 sm:px-4">
          <Modal.Title className="text-2xl font-bold">Add Comment</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmitComment} className="p-2">
          <Modal.Body className="p-8">
            <Form.Group className="mb-6">
              <Form.Label className="font-bold text-gray-700">Comment *</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={commentForm.content}
                onChange={(e) => setCommentForm(prev => ({ ...prev, content: e.target.value }))}
                placeholder="Write your comment here..."
                required
                className="rounded-xl border-purple-200 focus:ring-4 focus:ring-purple-300 focus:border-purple-500"
              />
            </Form.Group>
            <Form.Group className="mb-6">
              <Form.Label className="font-bold text-gray-700">Attachment (Optional)</Form.Label>
              <Form.Control
                type="file"
                onChange={handleFileChange}
                accept="image/*,.pdf,.doc,.docx"
                className="file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
              />
              <Form.Text className="text-gray-500">
                Supported formats: Images, PDF, DOC, DOCX (Max 5MB)
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer className="border-0 px-8 py-6 bg-gray-50">
               <Row className="w-100 g-2">
                <Col xs={6}>
            <Button bsPrefix="custom-hamburger" variant="secondary" onClick={() => setShowCommentModal(false)} className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2">
              Cancel
            </Button>
               </Col>
                        <Col xs={6}>
            <Button bsPrefix="custom-hamburger" variant="primary" type="submit" disabled={submitting} className="w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 ">
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
              </Col>
            </Row>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ForumDetail;